/* Client-side offline queue for grade saves.
 * Stores POST /api/grades/bulk-upsert payloads when the network is down;
 * replays them (in order) once the browser comes back online.
 */
import axios from 'axios';

const KEY = 'gs.offline.gradeQueue.v1';

const load = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '[]');
  } catch { return []; }
};

const save = (q) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(q));
    window.dispatchEvent(new Event('gs.offlineQueueChanged'));
  } catch { /* quota exceeded — silent */ }
};

export const queueLength = () => load().length;

export const enqueueGradeSave = (payload) => {
  const q = load();
  q.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    payload,
    createdAt: new Date().toISOString(),
  });
  save(q);
  return q.length;
};

export const clearQueue = () => save([]);

const buildBackendUrl = () => {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  return `${base}/api/grades/bulk-upsert`;
};

let _running = false;

export const flushQueue = async () => {
  if (_running) return { ok: 0, failed: 0, skipped: true };
  const q = load();
  if (q.length === 0) return { ok: 0, failed: 0, skipped: true };
  _running = true;
  const url = buildBackendUrl();
  let ok = 0, failed = 0;
  const remaining = [];
  for (const item of q) {
    try {
      await axios.post(url, item.payload, { withCredentials: true });
      ok++;
    } catch (e) {
      // Only keep if it's a network error (retry later).
      // Server-side 4xx/5xx (validation errors) → drop and count as failed.
      if (!e.response) {
        remaining.push(item);
      }
      failed++;
    }
  }
  save(remaining);
  _running = false;
  return { ok, failed };
};

/** Auto-flush hook: listens for `online` event and flushes.
 * Also runs on load if the queue isn't empty. Returns an unsubscribe fn.
 */
export const startAutoFlush = (onDone) => {
  const trigger = async () => {
    if (!navigator.onLine) return;
    const res = await flushQueue();
    if (res.ok > 0 && onDone) onDone(res);
  };
  window.addEventListener('online', trigger);
  // Try once on load in case we came back online while offline
  setTimeout(trigger, 1500);
  return () => window.removeEventListener('online', trigger);
};
